<template>
    <tbody>
        <template v-for="(list, slug) in ticketsList">
            <tr v-for="(ticket, sector) in list" v-show="slug === active.slug_en" :data-sector="parseInt(sector)" :class="active.slug_en + '-sector-' + parseInt(sector)" :key="`${slug}_${sector}`">
                <td>
                    <div class="sector-text">
                        <div class="sector">{{ trans.tickets.table.sector }} {{ parseInt(sector) }}</div>
                        <div v-if="sector.split('@').length > 1" class="line">
                            {{ trans.tickets.table.row }} {{ sector.split('@')[1] }}
                        </div>
                    </div>
                </td>

                <td v-for="(group, key) in dates" :key="`${slug}_${sector}_${key}`">
                    <div v-if="!isTicketAvailable(slug, sector, key)" class="soldout">
                        {{ trans.tickets.table.unavailable }}
                    </div>

                    <div class="soldout" v-if="isTicketRestricted(slug, sector, key)">
                        {{ ticket[key + 1].status_text || trans.tickets.table[ticket[key + 1].status] }}
                    </div>

                    <div class="wrap" v-show="isTicketSelectable(slug, sector, key)" v-if="Boolean(ticket[key + 1])">
                        <label>
                            <!-- Use directive v-if for hidde checkbox -->
                            <input
                                type="checkbox"
                                @change="toggle(ticket[key + 1])"
                                :value="ticket[key + 1].id"
                                name="Ticket"
                                :checked="Boolean(buyList[ticket[key + 1]])"
                            />

                            <tickets-table-price
                                :key="ticket[key + 1].id"
                                :promotionalInfo="discount"
                                :ticket="ticket[key + 1]"
                                :outlet="outlet"
                                :currency="trans.tickets.table.currency">
                            </tickets-table-price>

                            <tickets-table-tooltip
                                :key="'tooltip' + ticket[key + 1].id"
                                position="top"
                                :mobile="false"
                                :ticket="ticket[key + 1]"
                                :config="config"
                                :icons="trans.tickets.table.icons">
                            </tickets-table-tooltip>
                        </label>
                    </div>
                </td>
            </tr>
        </template>
    </tbody>
</template>

<style lang="scss">
    @import '../../../sass/variables';

    @each $section, $color in $tabs-bookers {
        .tab-content-#{$section} {
            label.active,
            tr td:hover label {
                &:before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: $color;
                    z-index: 0;
                }

                .model-tooltip,
                .english-tooltip {
                    color: $color;
                }
            }
        }
    }
</style>

<style lang="scss" scoped>
    caption {
        margin: .5em 0 .75em;
        font-size: 1.5em;
    }

    tr {
        background: white;
        border: 1px solid var(--color-gray-light);
        border-width: 1px 0 1px 0;
        padding: 10px;

        &:first-child {
            border: 0;
        }
    }

    td {
        padding: 1rem .2rem;
        text-align: center;
        transition: all .2s;
        font-size: 1.1rem;
        color: var(--color-gray-dark);
        width: calc(100% / 6);
        padding: 0;
        position: relative;

        label,
        .soldout,
        .sector-text {
            padding: 15px 10px;
            border-top: 1px solid transparent;
            width: 100%;
        }

        .soldout {
            opacity: .5;
        }

        label {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-wrap: wrap;
            margin: 0;
            cursor: pointer;
        }

        input {
            margin-right: 5px;
        }

        input,
        &.english {
            position: relative;
        }

        .wrap {
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    .sector-text {
        .sector {
            display: block;
        }

        .line {
            background: var(--color-gray-light);
            border-radius: .5rem;
            display: inline-block;
            font-size: 0.7rem;
            margin-top: .33rem;
            padding: 3px 9px;
        }
    }

    label.active,
    td:hover label {
        color: #FFF;
    }
</style>

<script>
    import TicketsTableTooltip from './TicketsTableTooltip.vue'

    export default {
        props: {
            outlet: Boolean,
            toggle: Function,
            discount: {
                type: Object,
                required: false,
            },
            config: {
                type: Object,
                required: true,
            },
            dates: {
                type: Array,
                required: true,
            },
            active: {
                type: Object,
                required: true,
            },
            buyList: {
                type: Object,
                required: true,
            },
            tickets: {
                type: Object,
                required: true,
            },
            trans: {
                type: Object,
                required: true,
            }
        },
        components: {
            TicketsTableTooltip
        },
        computed: {
            ticketsList() {
                const ticketsList = this.tickets

                for (const type in ticketsList) {
                    for (const sector in ticketsList[type]) {
                        const countItems = Object.keys(ticketsList[type][sector]).length
                        const countSoldout = this.getSouldoutItemsSum(ticketsList[type][sector])

                        if (this.discount && this.discount.percent > 0) {
                            for (const ticket in ticketsList[type][sector]) {
                                const price = ticketsList[type][sector][ticket].price
                                const withDiscount = Math.floor(price - (price * this.discount.percent / 100))

                                ticketsList[type][sector][ticket].price = withDiscount
                                ticketsList[type][sector][ticket].price_ref = price
                            }
                        }

                        if (countItems === countSoldout) {
                            delete ticketsList[type][sector]
                        }

                    }
                }

                return this.sortSectors(ticketsList)
            },
        },
        methods: {
            sortSectors(ticketsList) {
                const sorted = {}

                for (const type in ticketsList) {
                    sorted[type] = {}

                    const sectorKeys = Object.keys(ticketsList[type])
                    sectorKeys.sort((a, b) => {
                        const sectorA = parseInt(a.split('@')[0])
                        const sectorB = parseInt(b.split('@')[0])

                        if (sectorA !== sectorB) {
                            return sectorA - sectorB
                        }

                        const rowA = a.split('@')[1] || ''
                        const rowB = b.split('@')[1] || ''

                        return rowA.localeCompare(rowB)
                    })

                    sectorKeys.forEach(sectorKey => {
                        sorted[type][sectorKey] = ticketsList[type][sectorKey]
                    })
                }

                return sorted
            },
            getSouldoutItemsSum(items) {
                return Object.keys(items).reduce((acc, cur) => {
                    const isDisabled = ['on-request', 'disable'].includes(items[cur].status)

                    if (isDisabled && typeof acc !== 'undefined') {
                        return +acc + 1
                    }

                    return +acc
                }, 0)
            },
            isTicketAvailable(slug, sector, key) {
                return Boolean(this.ticketsList[slug][sector][key + 1]);
            },
            isTicketRestricted(slug, sector, key) {
                const ticket = this.ticketsList[slug][sector][key + 1];
                return ticket && [
                    'disable',
                    'on-request',
                    'programmed',
                    'available-soon',
                ].includes(ticket.status);
            },
            isTicketSelectable(slug, sector, key) {
                return this.isTicketAvailable(slug, sector, key) && !this.isTicketRestricted(slug, sector, key);
            },
        }
    }
</script>
